/**
 * Schema-driven asset config resolution + shared helpers.
 *
 * The schema (field definitions + layout + item title mask) is owned by the
 * TENANT and lives on `tenant.publicMetadata.assetConfig`. SKUs and items store
 * only the field VALUES (flat on their publicMetadata, keyed by field id): a SKU
 * is asked for the fields at creation, and each item copies them at registration.
 */

import { AssetConfig, AssetField, SkuOverviewImage } from '../types/asset-config';

interface WithPublicMetadata {
  publicMetadata?: Record<string, any> | null;
}

interface WithTitle extends WithPublicMetadata {
  title?: string;
}

/**
 * Schema field VALUES live under `publicMetadata.details` (keyed by field id) on
 * both SKUs and items — matching the mobile app's convention.
 */
export const DETAILS_KEY = 'details';

/**
 * publicMetadata keys the registration form manages structurally (size, vertical,
 * and the schema field values under `details`). Everything else is free-form
 * "additional metadata" edited via the gear dialog (e.g. provenance).
 */
export const STRUCTURED_METADATA_KEYS = ['widthCm', 'heightCm', 'vertical', DETAILS_KEY];

/** The free-form (non-structured) part of a publicMetadata object. */
export const getExtraMetadata = (publicMetadata?: Record<string, any>): Record<string, any> =>
  Object.fromEntries(
    Object.entries(publicMetadata ?? {}).filter(([key]) => !STRUCTURED_METADATA_KEYS.includes(key)),
  );

/** Whether any free-form additional metadata is present. */
export const hasExtraMetadata = (publicMetadata?: Record<string, any>): boolean =>
  Object.keys(getExtraMetadata(publicMetadata)).length > 0;

/** The details object of a SKU/item (the schema field values). */
export const getDetails = (source?: WithPublicMetadata): Record<string, any> =>
  (source?.publicMetadata?.[DETAILS_KEY] as Record<string, any>) ?? {};

/** A single schema field value from `publicMetadata.details`. */
export const getDetail = (source: WithPublicMetadata | undefined, fieldId: string): any =>
  getDetails(source)[fieldId];

/** Resolve the tenant's asset config. Returns undefined when the tenant has none. */
export const getAssetConfig = (tenant?: WithPublicMetadata): AssetConfig | undefined =>
  tenant?.publicMetadata?.assetConfig as AssetConfig | undefined;

/** The tenant's top-bar branding, if configured (image URL or wordmark text). */
export const getBranding = (config?: AssetConfig): AssetConfig['branding'] => config?.branding;

/** The field flagged as the DB `title` column (label-only override), if any. */
export const getTitleField = (config?: AssetConfig): AssetField | undefined =>
  config?.assetSchema?.fields?.find((f) => f.isTitle);

/** Label to show for the title input (the title field's label, else "Title"). */
export const titleFieldLabel = (config?: AssetConfig): string =>
  getTitleField(config)?.label ?? 'Title';

/** Whether a built-in optional item field (subtitle/externalId) is shown (default true). */
export const isSystemFieldVisible = (
  config: AssetConfig | undefined,
  field: 'subtitle' | 'externalId',
): boolean => config?.systemFields?.[field] !== false;

/**
 * Overview image slots to capture during SKU creation. Defaults to a single
 * "overview" slot (the pre-existing behavior) when the tenant configures none.
 */
export const getSkuOverviewImages = (config?: AssetConfig): SkuOverviewImage[] => {
  const list = config?.skuOverviewImages;
  return list && list.length > 0
    ? list
    : [{ id: 'overview', label: 'Overview', thumbnail: 'thumbnail' }];
};

/** Whether image classification is configured (enabled + a vectorizer URL). */
export const isClassificationEnabled = (config?: AssetConfig): boolean =>
  Boolean(config?.classification?.enabled && config?.classification?.url);

/**
 * The photo-stream crop method to send to the camera (stream `?crop=` + snap body),
 * or undefined when disabled. The camera controller draws the box + crops to it.
 */
export const getPhotoStreamCrop = (config?: AssetConfig): string | undefined => {
  const crop = config?.photoStreamCrop;
  return crop?.enabled && crop.method ? crop.method : undefined;
};

/**
 * Whether the camera capture UI shows the 90-degree rotate button. The actual angle is a
 * per-camera operator setting (see `settings.cameraRotation`), not part of the tenant config.
 */
export const isPhotoStreamRotateEnabled = (config?: AssetConfig): boolean =>
  config?.photoStreamRotate?.enabled === true;

/** Vectorizer endpoint for classification (undefined when not configured). */
export const getClassificationUrl = (config?: AssetConfig): string | undefined =>
  config?.classification?.url;

/**
 * Which captured overview image (by slot id) to vectorize for classification.
 * Defaults to the first overview slot when unset.
 */
export const getClassificationOverviewImageId = (config?: AssetConfig): string =>
  config?.classification?.overviewImageId ?? getSkuOverviewImages(config)[0].id;

/**
 * Placeholder the frontend leaves in a registered item's title where the fingerprint number
 * goes; the backend replaces it with the atomically-assigned per-SKU sequence at registration.
 */
export const SEQ_PLACEHOLDER = '{{seq}}';

/** The item-level field flagged as the serial number / identifier, if the tenant configured one. */
export const getIdentifierField = (config?: AssetConfig): AssetField | undefined =>
  config?.assetSchema?.fields?.find((f) => f.isIdentifier);

/**
 * The identification meta line shown under an item title (the item header and item list rows):
 * "SN: <serial or —> | FN: <zero-padded fingerprint number> | UID: <uid>", then the external id and
 * — only when `includeSku` — the SKU title. Matches the Origin result page. Parts with no value are
 * dropped. `includeSku` is off for list rows because the table has its own SKU Title column.
 */
export const getItemMetaLine = (
  config: AssetConfig | undefined,
  item: {
    uniqueId?: string;
    externalId?: string | null;
    publicMetadata?: Record<string, any>;
    sku?: { title?: string } | null;
  },
  options?: { includeSku?: boolean },
): string => {
  const identifierField = getIdentifierField(config);
  const fn = (item.publicMetadata as { skuSequence?: number } | undefined)?.skuSequence;
  const parts: string[] = [];
  if (identifierField) parts.push(`SN: ${getDetail(item, identifierField.id) || '—'}`);
  if (fn != null) parts.push(`FN: ${String(fn).padStart(3, '0')}`);
  if (item.uniqueId) parts.push(`UID: ${item.uniqueId}`);
  if (item.externalId) parts.push(`External ID: ${item.externalId}`);
  if (options?.includeSku && item.sku?.title) parts.push(`SKU Title: ${item.sku.title}`);
  return parts.join(' | ');
};

/**
 * Resolve the `<counter>` token for a registered item: the operator's serial (the identifier
 * field's value) when present, otherwise the {{seq}} placeholder that the backend fills with
 * the fingerprint number. `itemValues` carries the item's `publicMetadata.details`.
 */
export const resolveItemCounter = (
  config: AssetConfig | undefined,
  itemValues: WithPublicMetadata | undefined,
): string => {
  const idField = getIdentifierField(config);
  const serial = idField ? getDetail(itemValues, idField.id) : undefined;
  const hasSerial = serial !== undefined && serial !== null && String(serial).trim() !== '';
  // Serial (printed) -> "#<serial>"; otherwise the fingerprint-number placeholder prefixed "FN"
  // (the backend swaps the placeholder for the zero-padded sequence, e.g. "FN001").
  return hasSerial ? `#${String(serial).trim()}` : `FN${SEQ_PLACEHOLDER}`;
};

/**
 * Build an item title from the tenant's `itemTitleMask`, interpolating `<counter>` and
 * `<field_id>` tokens. Attribute-field tokens resolve from the source's publicMetadata; the
 * title-field token resolves from `.title`. `counter` is the resolved counter value — a number
 * for legacy callers, or the serial / {{seq}} placeholder for registration (see
 * `resolveItemCounter`). Returns undefined when no mask is configured.
 */
export const applyTitleMask = (
  config: AssetConfig | undefined,
  valuesSource: WithTitle | undefined,
  counter: string | number,
): string | undefined => {
  const mask = config?.itemTitleMask;
  if (!mask) {
    return undefined;
  }
  const titleFieldId = getTitleField(config)?.id;
  const values = getDetails(valuesSource);
  return mask.replace(/<([^>]+)>/g, (_match, token: string) => {
    if (token === 'counter') {
      return String(counter);
    }
    // Resolve from `details` first (works whether the source is the SKU or a registering item,
    // which carries the copied field values); fall back to `.title` for the title field.
    let value = values[token];
    if ((value === undefined || value === null) && titleFieldId && token === titleFieldId) {
      value = valuesSource?.title;
    }
    return value === undefined || value === null ? '' : String(value);
  });
};

/**
 * The configurable attribute fields — every schema field except the one flagged
 * as the title (which is rendered as the relabeled Title input, not an attribute).
 */
export const dynamicFields = (config?: AssetConfig): AssetField[] =>
  (config?.assetSchema?.fields ?? []).filter((f) => !f.isTitle);

/**
 * The attribute fields asked on the SKU (create/edit) — the dynamic fields minus
 * the `itemOnly` ones, which vary per item and are entered at registration instead.
 */
export const skuFields = (config?: AssetConfig): AssetField[] =>
  dynamicFields(config).filter((f) => !f.itemOnly);

/** The per-item attribute fields entered at registration (item-only, incl. any serial). */
export const itemOnlyFields = (config?: AssetConfig): AssetField[] =>
  dynamicFields(config).filter((f) => f.itemOnly);

/** Look up a field definition by id. */
export const findField = (config: AssetConfig | undefined, id: string): AssetField | undefined =>
  config?.assetSchema?.fields?.find((f) => f.id === id);

/** Display label with the unit appended for number fields (e.g. "Weight (g)"). */
export const fieldLabel = (field: AssetField): string =>
  field.type === 'number' && field.unit ? `${field.label} (${field.unit})` : field.label;

/** Human-readable value for a field (adds a unit suffix for number fields). */
export const formatFieldValue = (field: AssetField | undefined, value: unknown): string => {
  if (value === undefined || value === null || value === '') {
    return '—';
  }
  const base = String(value);
  if (field?.type === 'boolean') {
    if (base === 'true') return 'Yes';
    if (base === 'false') return 'No';
    return base;
  }
  if (field?.type === 'number' && field.unit) {
    return `${base} ${field.unit}`;
  }
  return base;
};

/** Normalise an OCR/model boolean-ish value to the stored 'true'/'false' (or '' if unclear). */
export const normalizeBooleanValue = (value: unknown): string => {
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  const v = String(value).trim().toLowerCase();
  if (['true', 'yes', 'y', '1'].includes(v)) return 'true';
  if (['false', 'no', 'n', '0'].includes(v)) return 'false';
  return '';
};
